import {
  ConfigurableAction,
  ConfigurableLink,
  Footer as PlatformFooter,
} from '@interhyp/platform-navigation';
import { ConsentManagerOpen$ } from '@interhyp/shared-consent-manager';
import { useTranslationContext } from '@interhyp/util-translation';
import { useClientTheme } from '@platform/theme';
import React, { useMemo } from 'react';

import { COMPANY_ID_INTERHYP } from '../../constants/companies';
import { useIsEmbedded } from '../../hooks/useIsEmbedded';

type ConfigurableLinkType = ReturnType<
  typeof useClientTheme
>['configurableLinks'][0]['configurableLinktype'];

const labels = {
  imprint: 'Impressum',
  privacy: 'Datenschutz',
  terms_and_conditions: 'Nutzungsbedingungen',
  imprint_professional: 'Impressum',
  imprint_customer: 'Impressum',
  privacy_professional: 'Datenschutz',
  privacy_customer: 'Datenschutz',
  terms_and_conditions_professional: 'Nutzungsbedingungen',
  terms_and_conditions_customer: 'Nutzungsbedingungen',
} satisfies Record<ConfigurableLinkType, unknown>;

export function Footer(): React.ReactElement | null {
  const { translate: t } = useTranslationContext();
  const { companyId, companyName, configurableLinks: themeLinks } = useClientTheme();
  const isEmbedded = useIsEmbedded();

  const copyrightInfo = useMemo(
    () => ({
      companyName,
    }),
    [companyName],
  );

  const configurableLinks = useMemo(() => {
    const links: (ConfigurableLink | ConfigurableAction)[] = themeLinks
      .sort((linkA, linkB) =>
        labels[linkA.configurableLinktype].localeCompare(labels[linkB.configurableLinktype]),
      )
      .map(link => ({
        href: link.hrefvalue,
        label: labels[link.configurableLinktype],
      }));

    // Only show the consent link for interhyp (like in app-interhyp-home before)
    if (`${companyId}` === COMPANY_ID_INTERHYP) {
      links.push({
        action: () => {
          ConsentManagerOpen$.next(true);
        },
        label: 'Cookie-Einstellungen ändern',
      });
    }

    return links;
  }, [themeLinks, companyId]);

  if (isEmbedded) {
    return null;
  }

  return (
    <PlatformFooter
      copyrightInfo={copyrightInfo}
      configurableLinks={configurableLinks}
      description={t('footer.awardDescription')}
    />
  );
}
